import { ReportingPeriod, RiskMetrics, StatisticsView, StatisticsWindow, TrailingReturn } from '@ndvr/reporting-api';

import { EM_DASH, toNumber } from '../reporting/format';
import { GlossaryKey } from '../reporting/glossary';

// Column-group labels for the statistics windows, in the order the server returns them.
export const PERIOD_LABELS: Partial<Record<ReportingPeriod, string>> = {
    QTD: 'QTD',
    YTD: 'YTD',
    ONE_YEAR: '1 Year',
    THREE_YEAR: '3 Year',
    FIVE_YEAR: '5 Year',
    TEN_YEAR: '10 Year',
    INCEPTION_TO_DATE: 'Inception to Date',
};

export const periodLabel = (period: ReportingPeriod): string => PERIOD_LABELS[period] ?? period;

// A benchmark's return over exactly the household's window for the same period, from that
// benchmark's own statistics; null (an em dash) when its window differs. The server anchors each
// benchmark separately — the latest month and the first shared month of the household's and that
// benchmark's data — so a lagging or younger benchmark's "1 Year" or inception can cover different
// months than the household's figure beside it, and non-comparable returns must not sit side by side.
export const comparableBenchmarkReturn = (benchmarkView: StatisticsView, householdWindow: TrailingReturn) => {
    const own = benchmarkView.trailingReturns.find((candidate) => candidate.period === householdWindow.period);
    if (!own || own.fromDate !== householdWindow.fromDate || own.toDate !== householdWindow.toDate) {
        return null;
    }
    return own.benchmarkReturnPercent;
};

// The trailing returns table fits up to seven columns, so inception gets the design's
// abbreviated header ("Incept.") instead of the full label the wider tables use.
export const trailingPeriodLabel = (period: ReportingPeriod): string =>
    period === ReportingPeriod.INCEPTION_TO_DATE ? 'Incept.' : periodLabel(period);

/** "18.9%" — for figures the API already expresses in percent (returns, deviations). */
export const statPercent = (value: string | null | undefined): string => {
    const n = toNumber(value);
    return n === null ? EM_DASH : `${n.toFixed(1)}%`;
};

/** "1.42" — plain ratios (Sharpe, beta, capture, …); em dash when undefined (e.g. zero variance). */
export const statRatio = (value: string | null | undefined): string => {
    const n = toNumber(value);
    return n === null ? EM_DASH : n.toFixed(2);
};

/** "3.4" — percent-scale figures the design shows without a % sign (alpha, tracking error, R²). */
export const statBareValue = (value: string | null | undefined): string => {
    const n = toNumber(value);
    return n === null ? EM_DASH : n.toFixed(1);
};

// Rows of the risk measurements table: label + which RiskMetrics field feeds it and how
// it is formatted. One definition drives both the account and benchmark columns.
export const RISK_MEASUREMENT_ROWS: ReadonlyArray<{
    label: string;
    field: keyof RiskMetrics;
    format: (value: string | null | undefined) => string;
    // GLOSSARY_TERMS key behind the row's info icon.
    glossaryKey: GlossaryKey;
}> = [
    { label: 'Return', field: 'returnPercent', format: statPercent, glossaryKey: 'ret' },
    { label: 'Standard Deviation', field: 'standardDeviation', format: statPercent, glossaryKey: 'sd' },
    { label: 'Sharpe Ratio', field: 'sharpeRatio', format: statRatio, glossaryKey: 'sharpe' },
    { label: 'Sortino Ratio', field: 'sortinoRatio', format: statRatio, glossaryKey: 'sortino' },
    { label: 'Downside Deviation', field: 'downsideDeviation', format: statPercent, glossaryKey: 'dd' },
];

// Rows of the relative statistics table: label + accessor + format, matching the design's
// mixed precision (ratios to 2 decimals, percent-scale figures to 1).
export const RELATIVE_STATISTIC_ROWS: ReadonlyArray<{
    label: string;
    value: (window: StatisticsWindow) => string;
    // GLOSSARY_TERMS key behind the row's info icon; none where the glossary has no entry.
    glossaryKey?: GlossaryKey;
}> = [
    { label: 'Beta', glossaryKey: 'beta', value: (w) => statRatio(w.relativeStatistics?.beta) },
    { label: 'Alpha', value: (w) => statBareValue(w.relativeStatistics?.alpha) },
    { label: 'Information Ratio', glossaryKey: 'ir', value: (w) => statRatio(w.relativeStatistics?.informationRatio) },
    { label: 'Tracking Error', glossaryKey: 'te', value: (w) => statBareValue(w.relativeStatistics?.trackingError) },
    { label: 'Treynor Ratio', glossaryKey: 'treynor', value: (w) => statRatio(w.relativeStatistics?.treynorRatio) },
    { label: "Jensen's Alpha", glossaryKey: 'alpha', value: (w) => statBareValue(w.relativeStatistics?.jensensAlpha) },
    {
        label: 'Upside Capture Ratio',
        glossaryKey: 'up',
        value: (w) => statRatio(w.relativeStatistics?.upsideCaptureRatio),
    },
    {
        label: 'Downside Capture Ratio',
        glossaryKey: 'down',
        value: (w) => statRatio(w.relativeStatistics?.downsideCaptureRatio),
    },
    { label: 'Covariance', value: (w) => statRatio(w.relativeStatistics?.covariance) },
    { label: 'R Squared', glossaryKey: 'r2', value: (w) => statBareValue(w.relativeStatistics?.rSquared) },
];
